// IA de Pendências Acadêmicas - Componente Principal
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Brain, LayoutDashboard, Table, Users, BookOpen, GraduationCap,
  AlertTriangle, Settings, Download, ArrowLeft, RefreshCw, Loader2,
  Clock, Zap, Sparkles, GhostIcon
} from 'lucide-react';

// Sub-componentes - Fase 1
import { DashboardPendencias } from './DashboardPendencias';
import { ListaPendencias } from './ListaPendencias';
import { VisaoProfessor } from './VisaoProfessor';
import { VisaoTurma } from './VisaoTurma';
import { VisaoDisciplina } from './VisaoDisciplina';
import { AlertasIA } from './AlertasIA';
import { ConfiguracaoPrazos } from './ConfiguracaoPrazos';
// Sub-componentes - Fase 2 (Blocos 10-15)
import { ReguaAtraso } from './ReguaAtraso';
import { AcoesRapidas } from './AcoesRapidas';
import { ResumoDetalhado } from './ResumoDetalhado';
import { ReconciliarOrfaos } from './ReconciliarOrfaos';
import { PrevisaoFechamento } from './PrevisaoFechamento';

const API_URL = window.location.origin;

export default function PendenciasAcademicas() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Filtros selecionados
  const [selectedYear, setSelectedYear] = useState('2026');
  const [selectedTrimestre, setSelectedTrimestre] = useState('1º Trimestre');
  const [showOrfaos, setShowOrfaos] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    loadFiltros();
  }, []);
  
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);
  
  const loadFiltros = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pendencias-academicas/filtros?year=2026`);
      const data = await res.json();
      setFiltros(data);
    } catch (error) {
      console.error('Erro ao carregar filtros:', error);
      toast.error('Erro ao carregar filtros');
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleRecalcular = async () => {
    try {
      const r = await fetch(`${API_URL}/api/pendencias-academicas/recalcular?year=${selectedYear}&trimestre=${encodeURIComponent(selectedTrimestre)}`, { method: 'POST' });
      if (!r.ok) throw new Error();
      setRefreshKey(k => k + 1);
      toast.success('Pendências recalculadas com base nos lançamentos reais');
    } catch {
      toast.error('Falha ao recalcular pendências');
    }
  };
  
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pendencias-academicas/exportar?year=${selectedYear}&trimestre=${encodeURIComponent(selectedTrimestre)}&formato=csv`
      );
      const data = await res.json();
      
      if (data.csv) {
        const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `pendencias_${selectedTrimestre.replace(/\s/g, '_')}_${selectedYear}.csv`;
        link.click();
        toast.success(`Exportação concluída: ${data.total} registros`);
      }
    } catch (error) {
      console.error('Erro na exportação:', error);
      toast.error('Erro ao exportar dados');
    } finally {
      setExporting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-green-500 animate-pulse mx-auto" />
            <Brain className="w-10 h-10 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-slate-600 mt-4 text-lg">Carregando IA de Pendências...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-slate-50" data-testid="pendencias-academicas">
      {/* Header Premium */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-purple-200/50 shadow-sm">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Título */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/v2/academico/notas')}
                className="text-purple-600 hover:bg-purple-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-green-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <Brain className="w-7 h-7 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                  IA de Pendências
                  <Badge className="bg-gradient-to-r from-purple-500 to-green-500 text-white text-xs">
                    Auditoria
                  </Badge>
                </h1>
                <p className="text-slate-500 text-sm">Monitoramento inteligente de lançamentos</p>
              </div>
            </div>
            
            {/* Filtros Globais */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Ano */}
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[100px] bg-white border-purple-200 text-slate-700">
                  <SelectValue placeholder="Ano" />
                </SelectTrigger>
                <SelectContent className="bg-white border-purple-200">
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Trimestre */}
              <Select value={selectedTrimestre} onValueChange={setSelectedTrimestre}>
                <SelectTrigger className="w-[160px] bg-white border-purple-200 text-slate-700">
                  <SelectValue placeholder="Trimestre" />
                </SelectTrigger>
                <SelectContent className="bg-white border-purple-200">
                  <SelectItem value="1º Trimestre">1º Trimestre</SelectItem>
                  <SelectItem value="2º Trimestre">2º Trimestre</SelectItem>
                  <SelectItem value="3º Trimestre">3º Trimestre</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                size="sm"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={handleRecalcular}
                data-testid="btn-recalcular-pendencias"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Recalcular Pendências
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                onClick={() => setShowOrfaos(true)}
                data-testid="btn-reconciliar-orfaos"
              >
                <GhostIcon className="w-4 h-4 mr-2" />
                Notas Órfãs
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-purple-300 text-purple-600 hover:bg-purple-50"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Exportar
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-6 py-6">
        <div className="mb-3 text-[11px] text-slate-500 italic flex items-center gap-1.5 px-2" data-testid="pendencias-aviso">
          <Brain className="w-3 h-3 text-purple-500" />
          Pendências calculadas com base nos lançamentos reais da Central de Notas.
        </div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="w-full max-w-5xl mx-auto mb-8 bg-white/80 border border-purple-200/50 p-1.5 rounded-2xl shadow-sm flex-wrap h-auto gap-1">
            <TabsTrigger 
              value="dashboard"
              className="flex-1 min-w-[100px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-purple-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-dashboard"
            >
              <LayoutDashboard className="w-4 h-4 mr-1" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger 
              value="lista"
              className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-purple-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-lista"
            >
              <Table className="w-4 h-4 mr-1" />
              Lista
            </TabsTrigger>
            <TabsTrigger 
              value="professor"
              className="flex-1 min-w-[90px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-professor"
            >
              <Users className="w-4 h-4 mr-1" />
              Professor
            </TabsTrigger>
            <TabsTrigger 
              value="turma"
              className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-600 data-[state=active]:to-emerald-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-turma"
            >
              <BookOpen className="w-4 h-4 mr-1" />
              Turma
            </TabsTrigger>
            <TabsTrigger 
              value="disciplina"
              className="flex-1 min-w-[90px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-amber-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-disciplina"
            >
              <GraduationCap className="w-4 h-4 mr-1" />
              Disciplina
            </TabsTrigger>
            <TabsTrigger 
              value="alertas"
              className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-pink-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-alertas"
            >
              <AlertTriangle className="w-4 h-4 mr-1" />
              Alertas
            </TabsTrigger>
            <TabsTrigger 
              value="config"
              className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-slate-600 data-[state=active]:to-slate-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-config"
            >
              <Settings className="w-4 h-4 mr-1" />
              Config
            </TabsTrigger>
            {/* Fase 2 - Novas Abas */}
            <TabsTrigger 
              value="regua"
              className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-indigo-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-regua"
            >
              <Clock className="w-4 h-4 mr-1" />
              Régua
            </TabsTrigger>
            <TabsTrigger 
              value="acoes"
              className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-acoes"
            >
              <Zap className="w-4 h-4 mr-1" />
              Ações
            </TabsTrigger>
            <TabsTrigger 
              value="resumo-ia"
              className="flex-1 min-w-[90px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-green-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-resumo-ia"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Resumo IA
            </TabsTrigger>
            <TabsTrigger 
              value="previsao"
              className="flex-1 min-w-[90px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-600 data-[state=active]:to-purple-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl transition-all py-2"
              data-testid="tab-previsao"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Previsão IA
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="dashboard">
            <DashboardPendencias 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
            />
          </TabsContent>
          
          <TabsContent value="lista">
            <ListaPendencias 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
              filtros={filtros}
            />
          </TabsContent>
          
          <TabsContent value="professor">
            <VisaoProfessor 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
            />
          </TabsContent>
          
          <TabsContent value="turma">
            <VisaoTurma 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
            />
          </TabsContent>
          
          <TabsContent value="disciplina">
            <VisaoDisciplina 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
            />
          </TabsContent>
          
          <TabsContent value="alertas">
            <AlertasIA 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
            />
          </TabsContent>
          
          <TabsContent value="config">
            <ConfiguracaoPrazos 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
            />
          </TabsContent>
          
          {/* Fase 2 - Novos Conteúdos */}
          <TabsContent value="regua">
            <ReguaAtraso 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
              filtros={filtros}
            />
          </TabsContent>
          
          <TabsContent value="acoes">
            <AcoesRapidas 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
              filtros={filtros}
            />
          </TabsContent>
          
          <TabsContent value="resumo-ia">
            <ResumoDetalhado 
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
            />
          </TabsContent>
          
          <TabsContent value="previsao">
            <PrevisaoFechamento
              year={parseInt(selectedYear)}
              trimestre={selectedTrimestre}
            />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Modal de Reconciliação de Notas Órfãs */}
      <ReconciliarOrfaos
        open={showOrfaos}
        onOpenChange={setShowOrfaos}
        onReconciled={() => setRefreshKey(k => k + 1)}
      />
    </div>
  );
}
